import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Typography } from "@mui/material";
import {
  useDeleteSiteMutation,
  useGetSitesQuery,
  useGetSiteDeleteSummaryQuery,
} from "../../features/sites/sitesApiSlice";
import Loading from "../spinners/Loading";
import { useDispatch } from "react-redux";
import { clearDialog, setDialog } from "../../features/dialog/dialogSlice";
import ErrorAlert from "../alerts/ErrorAlert";
import { apiSlice } from "../../app/api/apiSlice";

function DeleteSiteDialogContent({ dialogType, siteData }) {
  const { refetch } = useGetSitesQuery();
  const dispatch = useDispatch();
  const [serverRelated, setServerRelated] = useState(null);

  const [deleteSite, { isLoading, isError }] = useDeleteSiteMutation();
  const {
    data: deleteSummary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetSiteDeleteSummaryQuery(siteData?._id, {
    skip: !siteData?._id,
  });

  const related = useMemo(() => {
    return (
      serverRelated ||
      deleteSummary?.related || { members: 0, contracts: 0, events: 0 }
    );
  }, [deleteSummary?.related, serverRelated]);

  const hasRelations = useMemo(() => {
    if (serverRelated) return true;
    return Boolean(deleteSummary?.hasRelations);
  }, [deleteSummary?.hasRelations, serverRelated]);

  const handleDeleteSite = async ({ deleteRelated } = {}) => {
    const result = await deleteSite({
      id: siteData._id,
      deleteRelated,
    });
    if (result?.error?.status === 409) {
      setServerRelated(
        result?.error?.data?.related || {
          members: 0,
          contracts: 0,
          events: 0,
        },
      );
      return;
    }
    if (result?.data?.status === "success") {
      refetch();
      dispatch(apiSlice.util.resetApiState());
      dispatch(apiSlice.util.invalidateTags(["Tenant"]));
      dispatch(
        setDialog({
          title: siteData.siteName + " wurde erfolgreich gelöscht!",
          type: "SUCCESS",
          data: {
            successText: siteData.siteName + " wurde erfolgreich gelöscht!",
          },
        }),
      );
    }
  };

  if (isSummaryLoading) {
    return (
      <div style={{ minHeight: 300 }}>
        <Loading />
      </div>
    );
  }

  if (isSummaryError) {
    return (
      <div>
        <ErrorAlert errorMessage="Fehler beim Laden der Standort-Verknüpfungen" />
        <Button
          onClick={() => refetchSummary()}
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Erneut laden
        </Button>
        <Button
          onClick={() => dispatch(clearDialog())}
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
        >
          Abbrechen
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ minHeight: 300 }}>
        <Loading />
      </div>
    );
  }

  if (isError) {
    return <ErrorAlert errorMessage="Fehler beim Löschen des Standorts" />;
  }

  return (
    <div>
      <Typography variant="h3" id="success-dialog-description">
        Möchten Sie diesen Standort wirklich endültig löschen?
      </Typography>
      <table style={{ fontSize: "150%" }}>
        <tbody>
          <tr>
            <th>Standortname:</th>
            <th>{siteData.siteName}</th>
          </tr>
        </tbody>
      </table>
      {hasRelations && (
        <>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Dieser Standort hat noch verknüpfte Daten:
          </Typography>
          <table style={{ fontSize: "140%", marginTop: 8 }}>
            <tbody>
              <tr>
                <td>Mitglieder</td>
                <td>{related.members}</td>
              </tr>
              <tr>
                <td>Verträge</td>
                <td>{related.contracts}</td>
              </tr>
              <tr>
                <td>Veranstaltungen</td>
                <td>{related.events}</td>
              </tr>
            </tbody>
          </table>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Wenn Sie fortfahren, werden alle verknüpften Daten ebenfalls
            gelöscht.
          </Typography>
        </>
      )}
      <Button
        onClick={() => handleDeleteSite({ deleteRelated: hasRelations })}
        fullWidth
        variant="contained"
        color="error"
        sx={{
          mt: 2,
          fontSize: "125%",
        }}
      >
        {hasRelations ? "Standort & Daten löschen" : "Löschen"}
      </Button>
      {hasRelations && (
        <Button
          onClick={() => dispatch(clearDialog())}
          fullWidth
          variant="outlined"
          sx={{ mt: 2, mb: 1 }}
        >
          Abbrechen
        </Button>
      )}
    </div>
  );
}

export default DeleteSiteDialogContent;

DeleteSiteDialogContent.propTypes = {
  siteData: PropTypes.object,
  dialogType: PropTypes.string,
};
